import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Building2,
  User,
  Mail,
  Phone,
  DollarSign,
  Calendar,
  Tag,
  MessageSquare,
  Clock,
  Plus,
  Trash2,
  Send,
  CheckCircle,
  Video,
  FileText,
  ExternalLink,
  Edit2,
  Save,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

// Activity Types
const ACTIVITY_TYPES = {
  call: { icon: Phone, color: 'text-green-400', bg: 'bg-green-400/10', label: 'Call' },
  email: { icon: Mail, color: 'text-blue-400', bg: 'bg-blue-400/10', label: 'Email' },
  meeting: { icon: Video, color: 'text-purple-400', bg: 'bg-purple-400/10', label: 'Meeting' },
  note: { icon: MessageSquare, color: 'text-slate-400', bg: 'bg-slate-400/10', label: 'Note' },
  task: { icon: CheckCircle, color: 'text-orange-400', bg: 'bg-orange-400/10', label: 'Task' },
  stage_change: { icon: ArrowRight, color: 'text-cyan-400', bg: 'bg-cyan-400/10', label: 'Stage Change' },
  document: { icon: FileText, color: 'text-yellow-400', bg: 'bg-yellow-400/10', label: 'Document' }
};

// Deal Detail Modal
export const DealDetailModal = ({
  isOpen,
  onClose,
  deal,
  stages,
  onUpdate,
  onDelete,
  onAddActivity,
  API_URL
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [newActivity, setNewActivity] = useState({ type: 'note', content: '' });
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [submittingActivity, setSubmittingActivity] = useState(false);

  useEffect(() => {
    if (deal && isOpen) {
      setEditForm({
        name: deal.name || '',
        company: deal.company || '',
        contact_name: deal.contact_name || '',
        email: deal.email || '',
        phone: deal.phone || '',
        value: deal.value || 0,
        recurring: deal.recurring || false,
        priority: deal.priority || 'medium',
        stage: deal.stage || 'new_lead',
        tags: deal.tags || [],
        notes: deal.notes || '',
        expected_close_date: deal.expected_close_date || ''
      });
      fetchActivities();
    }
  }, [deal, isOpen]);

  const fetchActivities = async () => {
    if (!deal?.id || !API_URL) return;
    setLoadingActivities(true);
    try {
      const res = await fetch(`${API_URL}/api/crm/deals/${deal.id}/activities`);
      if (res.ok) {
        const data = await res.json();
        setActivities(data.activities || []);
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleSave = async () => {
    if (!editForm.name?.trim()) {
      toast.error('Deal name is required');
      return;
    }
    
    await onUpdate(deal.id, editForm);
    setIsEditing(false);
    toast.success('Deal updated');
  };

  const handleAddActivity = async () => {
    if (!newActivity.content.trim()) {
      toast.error('Activity content is required');
      return;
    }

    setSubmittingActivity(true);
    try {
      await onAddActivity(deal.id, newActivity);
      setNewActivity({ type: 'note', content: '' });
      await fetchActivities();
      toast.success('Activity added');
    } catch (error) {
      toast.error('Failed to add activity');
    } finally {
      setSubmittingActivity(false);
    }
  };

  const handleStageChange = async (newStage) => {
    await onUpdate(deal.id, { ...editForm, stage: newStage });
    setEditForm({ ...editForm, stage: newStage });
    // Add stage change activity
    await onAddActivity(deal.id, {
      type: 'stage_change',
      content: `Moved to ${stages.find(s => s.id === newStage)?.label || newStage}`
    });
    await fetchActivities();
  };

  const handleTagAdd = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      const newTag = e.target.value.trim();
      if (!editForm.tags.includes(newTag)) {
        setEditForm({ ...editForm, tags: [...editForm.tags, newTag] });
      }
      e.target.value = '';
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setEditForm({
      ...editForm,
      tags: editForm.tags.filter(t => t !== tagToRemove)
    });
  };

  if (!isOpen || !deal) return null;

  const currentStage = stages.find(s => s.id === editForm.stage);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-800 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-white/10"
        onClick={(e) => e.stopPropagation()}
        data-testid="deal-detail-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center space-x-4 flex-1">
            {isEditing ? (
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="text-xl font-bold text-white bg-transparent border-b border-neon-cyan focus:outline-none"
                placeholder="Deal name"
              />
            ) : (
              <h2 className="text-xl font-bold text-white">{deal.name}</h2>
            )}
            
            {/* Stage Selector */}
            <select
              value={editForm.stage}
              onChange={(e) => handleStageChange(e.target.value)}
              className="px-3 py-1 rounded-lg text-sm border-0 focus:outline-none focus:ring-2 focus:ring-neon-cyan"
              style={{ 
                backgroundColor: currentStage?.color + '33', 
                color: currentStage?.color 
              }}
            >
              {stages.map(stage => (
                <option key={stage.id} value={stage.id}>{stage.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-3 py-1.5 text-sm text-slate-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-3 py-1.5 text-sm bg-neon-cyan text-slate-900 rounded-lg hover:bg-neon-cyan/80 flex items-center space-x-1"
                >
                  <Save className="w-4 h-4" />
                  <span>Save</span>
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="px-3 py-1.5 text-sm bg-white/10 text-white rounded-lg hover:bg-white/20 flex items-center space-x-1"
              >
                <Edit2 className="w-4 h-4" />
                <span>Edit</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10">
          {['overview', 'activity', 'documents'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'text-neon-cyan border-b-2 border-neon-cyan'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-180px)]">
          {activeTab === 'overview' && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Left Column - Details */}
              <div className="space-y-4">
                <div className="glass-card p-4">
                  <h3 className="text-sm font-medium text-slate-400 mb-3">Deal Information</h3>
                  
                  <div className="space-y-3">
                    {/* Value */}
                    <div>
                      <label className="text-xs text-slate-500">Deal Value</label>
                      <div className="flex items-center space-x-2">
                        <DollarSign className="w-4 h-4 text-neon-green" />
                        {isEditing ? (
                          <>
                            <input
                              type="number"
                              value={editForm.value}
                              onChange={(e) => setEditForm({ ...editForm, value: parseFloat(e.target.value) || 0 })}
                              className="w-24 px-2 py-1 bg-white/5 rounded text-white text-lg font-bold"
                            />
                            <label className="flex items-center space-x-1 text-sm text-slate-400">
                              <input
                                type="checkbox"
                                checked={editForm.recurring}
                                onChange={(e) => setEditForm({ ...editForm, recurring: e.target.checked })}
                                className="rounded"
                              />
                              <span>/month</span>
                            </label>
                          </>
                        ) : (
                          <span className="text-lg font-bold text-neon-green">
                            {deal.value?.toLocaleString() || '0'}
                            {deal.recurring && <span className="text-sm text-slate-400">/mo</span>}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Priority */}
                    <div>
                      <label className="text-xs text-slate-500">Priority</label>
                      {isEditing ? (
                        <select
                          value={editForm.priority}
                          onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                          className="w-full px-2 py-1 bg-white/5 rounded text-white mt-1"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      ) : (
                        <div className={`inline-block px-2 py-0.5 rounded text-xs capitalize ${
                          deal.priority === 'urgent' ? 'bg-red-500/20 text-red-400' :
                          deal.priority === 'high' ? 'bg-orange-500/20 text-orange-400' :
                          deal.priority === 'medium' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          {deal.priority}
                        </div>
                      )}
                    </div>

                    {/* Expected Close Date */}
                    <div>
                      <label className="text-xs text-slate-500">Expected Close Date</label>
                      {isEditing ? (
                        <input
                          type="date"
                          value={editForm.expected_close_date?.slice(0, 10) || ''}
                          onChange={(e) => setEditForm({ ...editForm, expected_close_date: e.target.value })}
                          className="w-full px-2 py-1 bg-white/5 rounded text-white mt-1"
                        />
                      ) : (
                        <div className="flex items-center text-white mt-1">
                          <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                          {deal.expected_close_date 
                            ? new Date(deal.expected_close_date).toLocaleDateString()
                            : 'Not set'
                          }
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="glass-card p-4">
                  <h3 className="text-sm font-medium text-slate-400 mb-3">Contact Information</h3>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-slate-500">Company</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.company}
                          onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                          className="w-full px-2 py-1 bg-white/5 rounded text-white mt-1"
                          placeholder="Company name"
                        />
                      ) : (
                        <div className="flex items-center text-white mt-1">
                          <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                          {deal.company || '-'}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="text-xs text-slate-500">Contact Name</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.contact_name}
                          onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                          className="w-full px-2 py-1 bg-white/5 rounded text-white mt-1"
                          placeholder="Contact name"
                        />
                      ) : (
                        <div className="flex items-center text-white mt-1">
                          <User className="w-4 h-4 mr-2 text-slate-400" />
                          {deal.contact_name || '-'}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="text-xs text-slate-500">Email</label>
                      {isEditing ? (
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                          className="w-full px-2 py-1 bg-white/5 rounded text-white mt-1"
                          placeholder="email@example.com"
                        />
                      ) : (
                        <div className="flex items-center text-white mt-1">
                          <Mail className="w-4 h-4 mr-2 text-slate-400" />
                          {deal.email ? (
                            <a href={`mailto:${deal.email}`} className="text-neon-cyan hover:underline">
                              {deal.email}
                            </a>
                          ) : '-'}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="text-xs text-slate-500">Phone</label>
                      {isEditing ? (
                        <input
                          type="tel"
                          value={editForm.phone}
                          onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                          className="w-full px-2 py-1 bg-white/5 rounded text-white mt-1"
                          placeholder="(555) 123-4567"
                        />
                      ) : (
                        <div className="flex items-center text-white mt-1">
                          <Phone className="w-4 h-4 mr-2 text-slate-400" />
                          {deal.phone ? (
                            <a href={`tel:${deal.phone}`} className="text-neon-cyan hover:underline">
                              {deal.phone}
                            </a>
                          ) : '-'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Tags & Notes */}
              <div className="space-y-4">
                {/* Tags */}
                <div className="glass-card p-4">
                  <h3 className="text-sm font-medium text-slate-400 mb-3">Tags</h3>
                  
                  <div className="flex flex-wrap gap-2 mb-2">
                    {editForm.tags?.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-white/10 rounded-full text-xs text-white flex items-center space-x-1"
                      >
                        <span>{tag}</span>
                        {isEditing && (
                          <button
                            onClick={() => handleTagRemove(tag)}
                            className="text-slate-400 hover:text-red-400"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    ))}
                    {editForm.tags?.length === 0 && !isEditing && (
                      <span className="text-slate-500 text-sm">No tags</span>
                    )}
                  </div>
                  
                  {isEditing && (
                    <input
                      type="text"
                      placeholder="Add tag (press Enter)"
                      onKeyDown={handleTagAdd}
                      className="w-full px-2 py-1 bg-white/5 rounded text-white text-sm"
                    />
                  )}
                </div>

                {/* Notes */}
                <div className="glass-card p-4">
                  <h3 className="text-sm font-medium text-slate-400 mb-3">Notes</h3>
                  {isEditing ? (
                    <textarea
                      value={editForm.notes}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      rows={4}
                      className="w-full px-3 py-2 bg-white/5 rounded-lg text-white text-sm resize-none"
                      placeholder="Add notes about this deal..."
                    />
                  ) : (
                    <p className="text-slate-300 text-sm whitespace-pre-wrap">
                      {deal.notes || 'No notes yet'}
                    </p>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="glass-card p-4">
                  <h3 className="text-sm font-medium text-slate-400 mb-3">Quick Actions</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => deal.email && window.open(`mailto:${deal.email}`)}
                      disabled={!deal.email}
                      className="py-2 px-3 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg text-sm flex items-center justify-center space-x-2 disabled:opacity-50"
                    >
                      <Mail className="w-4 h-4" />
                      <span>Email</span>
                    </button>
                    <button
                      onClick={() => deal.phone && window.open(`tel:${deal.phone}`)}
                      disabled={!deal.phone}
                      className="py-2 px-3 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-sm flex items-center justify-center space-x-2 disabled:opacity-50"
                    >
                      <Phone className="w-4 h-4" />
                      <span>Call</span>
                    </button>
                    <button
                      className="py-2 px-3 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-sm flex items-center justify-center space-x-2"
                    >
                      <Video className="w-4 h-4" />
                      <span>Schedule</span>
                    </button>
                    <button
                      onClick={() => onDelete(deal)}
                      className="py-2 px-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm flex items-center justify-center space-x-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-4">
              {/* Add Activity */}
              <div className="glass-card p-4">
                <h3 className="text-sm font-medium text-slate-400 mb-3">Log Activity</h3>
                <div className="flex space-x-2 mb-3">
                  {Object.entries(ACTIVITY_TYPES).filter(([k]) => k !== 'stage_change').map(([key, config]) => (
                    <button
                      key={key}
                      onClick={() => setNewActivity({ ...newActivity, type: key })}
                      className={`px-3 py-1.5 rounded-lg text-xs flex items-center space-x-1 transition-colors ${
                        newActivity.type === key
                          ? `${config.bg} ${config.color}`
                          : 'bg-white/5 text-slate-400 hover:bg-white/10'
                      }`}
                    >
                      <config.icon className="w-3 h-3" />
                      <span>{config.label}</span>
                    </button>
                  ))}
                </div>
                <div className="flex space-x-2">
                  <textarea
                    value={newActivity.content}
                    onChange={(e) => setNewActivity({ ...newActivity, content: e.target.value })}
                    placeholder={`Add ${ACTIVITY_TYPES[newActivity.type]?.label || 'activity'}...`}
                    rows={2}
                    className="flex-1 px-3 py-2 bg-white/5 rounded-lg text-white text-sm resize-none"
                  />
                  <button
                    onClick={handleAddActivity}
                    disabled={submittingActivity || !newActivity.content.trim()}
                    className="px-4 py-2 bg-neon-cyan text-slate-900 rounded-lg hover:bg-neon-cyan/80 disabled:opacity-50 flex items-center space-x-1"
                  >
                    {submittingActivity ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        <span>Add</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Activity Timeline */}
              <div className="glass-card p-4">
                <h3 className="text-sm font-medium text-slate-400 mb-4">Activity Timeline</h3>
                
                {loadingActivities ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 text-neon-cyan animate-spin" />
                  </div>
                ) : activities.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No activities yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activities.map((activity, idx) => {
                      const config = ACTIVITY_TYPES[activity.type] || ACTIVITY_TYPES.note;
                      const Icon = config.icon;
                      
                      return (
                        <div key={activity.id || idx} className="flex space-x-3">
                          <div className={`p-2 rounded-lg ${config.bg} flex-shrink-0`}>
                            <Icon className={`w-4 h-4 ${config.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-white">{config.label}</span>
                              <span className="text-xs text-slate-500">
                                {new Date(activity.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-slate-300 mt-1">{activity.content}</p>
                            {activity.created_by && (
                              <p className="text-xs text-slate-500 mt-1">by {activity.created_by}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="glass-card p-4">
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="mb-2">No documents attached</p>
                <button className="text-neon-cyan hover:text-neon-cyan/80 text-sm">
                  + Attach Document
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DealDetailModal;
